import * as React from "react";
import { X } from "lucide-react";
import { Badge, cn } from "@shared/ui";

import { Trans } from "react-i18next";

export interface TagInputProps {
  value?: string[];
  onChange?: (tags: string[]) => void;
  placeholder?: string;
  className?: string;
  disabled?: boolean;
  inputAriaLabel?: string;
}

export const TagInput = React.forwardRef<HTMLInputElement, TagInputProps>(
  (
    { value = [], onChange, placeholder, className, disabled, inputAriaLabel },
    ref,
  ) => {
    const [inputValue, setInputValue] = React.useState("");
    const containerRef = React.useRef<HTMLDivElement>(null);

    const handleAddTag = (tag: string) => {
      if (disabled) return;
      const trimmed = tag.trim();
      const exists = value.some(
        (current) => current.toLowerCase() === trimmed.toLowerCase(),
      );
      if (trimmed && !exists) {
        const newTags = [...value, trimmed];
        onChange?.(newTags);
      }
      setInputValue("");
    };

    const handleRemoveTag = (tagToRemove: string) => {
      if (disabled) return;
      const newTags = value.filter((t) => t !== tagToRemove);
      onChange?.(newTags);
    };

    const handleKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
      if (e.key === "Enter" || e.key === ",") {
        e.preventDefault();
        e.stopPropagation();
        handleAddTag(inputValue);
      } else if (e.key === "Backspace" && !inputValue && value.length > 0) {
        handleRemoveTag(value[value.length - 1]);
      }
    };

    const handleBlur = () => {
      if (inputValue) {
        handleAddTag(inputValue);
      }
    };

    return (
      <div
        ref={containerRef}
        className={cn(
          "flex min-h-10 w-full flex-wrap gap-1.5 rounded-md border border-input bg-background px-3 py-2 text-sm ring-offset-background focus-within:ring-2 focus-within:ring-primary/50 focus-within:outline-none",
          disabled && "cursor-not-allowed opacity-50",
          className,
        )}
        onClick={() => {
          const input = containerRef.current?.querySelector("input");
          input?.focus();
        }}
      >
        {value.map((tag) => (
          <Badge
            key={tag}
            variant="secondary"
            className="flex items-center gap-1 px-2 py-0.5 transition-all hover:bg-secondary/80"
          >
            {tag}
            <button
              type="button"
              className="rounded-full outline-none focus:ring-1 focus:ring-ring"
              onClick={(e) => {
                e.stopPropagation();
                handleRemoveTag(tag);
              }}
              disabled={disabled}
            >
              <X className="h-3 w-3" />
              <span className="sr-only">
                <Trans i18nKey="ui:remove">{"Remove"}</Trans> {tag}
              </span>
            </button>
          </Badge>
        ))}
        <input
          ref={ref}
          aria-label={inputAriaLabel}
          className="flex-1 bg-transparent outline-none placeholder:text-muted-foreground disabled:cursor-not-allowed"
          value={inputValue}
          onChange={(e) => setInputValue(e.target.value)}
          onKeyDown={handleKeyDown}
          onBlur={handleBlur}
          placeholder={value.length === 0 ? placeholder : ""}
          disabled={disabled}
        />
      </div>
    );
  },
);

TagInput.displayName = "TagInput";
